// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { useGameStore } from '../store/gameStore';

export function useSessionRestoration(): void {
  const restoringSessionRef = useRef<string | null>(null);
  const { clearSession, setSession, setSessionRestorePending } = useGameStore();
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useParams();

  // Reconcile URL session and persisted session state before allowing gameplay views to connect
  useEffect(() => {
    const urlSessionId = urlParams.sessionId;
    const isGameView =
      location.pathname.startsWith('/lobby/') || location.pathname.startsWith('/game/');
    const {
      sessionId: currentSessionId,
      playerId: currentPlayerId,
      playerToken: currentPlayerToken,
    } = useGameStore.getState();

    if (!isGameView || !urlSessionId) {
      restoringSessionRef.current = null;
      setSessionRestorePending(false);
      return;
    }

    if (urlSessionId === currentSessionId && currentPlayerId && currentPlayerToken) {
      restoringSessionRef.current = null;
      setSessionRestorePending(false);
      return;
    }

    if (restoringSessionRef.current === urlSessionId) {
      return;
    }

    let cancelled = false;
    restoringSessionRef.current = urlSessionId;
    setSessionRestorePending(true);

    if (currentSessionId && currentSessionId !== urlSessionId) {
      logger.info('[MainApp] Clearing stale session before URL restore', {
        currentSessionId,
        urlSessionId,
      });
      clearSession();
      setSessionRestorePending(true);
    }

    api
      .getMySession(true)
      .then(response => {
        if (cancelled) {
          return;
        }

        if (response.active_session && response.active_session.session_id === urlSessionId) {
          const session = response.active_session as typeof response.active_session & {
            player_token?: string;
            display_name?: string;
          };

          if (!session.player_token) {
            logger.warn('[MainApp] Session restore missing player token', {
              sessionId: session.session_id,
            });
            navigate('/');
            return;
          }

          logger.info('[MainApp] Session restored from API', {
            sessionId: session.session_id,
            playerId: session.player_id,
          });
          setSession(
            session.session_id,
            session.room_code,
            session.player_id,
            session.player_token,
            session.display_name || session.player_id
          );
        } else {
          logger.debug('[MainApp] API returned different session or no session', {
            returnedSessionId: response.active_session?.session_id,
            urlSessionId,
          });
          navigate('/');
        }
      })
      .catch(err => {
        if (!cancelled) {
          logger.debug('[MainApp] Could not fetch session from API', {
            error: err,
          });
          navigate('/');
        }
      })
      .finally(() => {
        if (!cancelled && restoringSessionRef.current === urlSessionId) {
          restoringSessionRef.current = null;
          setSessionRestorePending(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    clearSession,
    location.pathname,
    navigate,
    setSession,
    setSessionRestorePending,
    urlParams.sessionId,
  ]);
}
